import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import moment from 'moment';
import { JournalProcessDefinition } from '../../model/journal-process-definition.model';
import { environment } from '../../../environments/environment';

/** Shape of a JournalProcessDefinition as returned by the frontend API (GET). */
interface JournalProcessDefinitionApiModel {
  journalProcessDefinition_ID: string;
  booking_Group_ID: string;
  name: string;
  bookingDescription?: string | null;
  institutionFinancialAdministrationNumber?: string | null;
  bookingNumber?: string | null;
  journalNumber?: string | null;
  journalType?: string | null;
  employmentSelection_ID?: string | null;
  applyWagePartioning: boolean;
  applyRedistribution: boolean;
  bookByEmployment: boolean;
  bookNegativeValuesIndicator: boolean;
  retroactiveCalculationInPayrollPeriod: boolean;
  startDate: string | Date | null;
  endDate: string | Date | null;
  memo_Value?: string | null;
}

/**
 * Shape of a JournalProcessDefinition sent to the frontend API (POST/PUT).
 * changeUser / changeDateTime are intentionally omitted: the backend fills
 * them automatically and ignores any value we would send.
 */
interface JournalProcessDefinitionApiPayload {
  journalProcessDefinition_ID: string;
  booking_Group_ID: string;
  name: string;
  bookingDescription: string | null;
  institutionFinancialAdministrationNumber: string | null;
  bookingNumber: string | null;
  journalNumber: string | null;
  journalType: string | null;
  employmentSelection_ID: string | null;
  applyWagePartioning: boolean;
  applyRedistribution: boolean;
  bookByEmployment: boolean;
  bookNegativeValuesIndicator: boolean;
  retroactiveCalculationInPayrollPeriod: boolean;
  startDate: string | null;
  endDate: string | null;
  memo_Value: string | null;
}

interface AddJournalProcessDefinitionResponse {
  data?: string;
  journalProcessDefinition_ID?: string;
  journalProcessDefinitionId?: string;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

@Injectable({
  providedIn: 'root',
})
export class JournalProcessDefinitionService {
  private apiUrl = environment.apiUrl + '/kbj-frontend/api/v1/JournalProcessDefinition';
  private readonly httpOptions = { withCredentials: true };
  private http: HttpClient = inject(HttpClient);

  /**
   * Returns all journal process definitions for the given booking group.
   * Uses withCredentials so cookies from a different subdomain are included.
   * Returns an empty array when the API responds with 404 or 204 No Content.
   */
  getByContext(bookingGroupId: string): Observable<JournalProcessDefinition[]> {
    return this.http
      .get<JournalProcessDefinitionApiModel[] | null>(
        this.apiUrl + '/ByContext/' + bookingGroupId,
        this.httpOptions,
      )
      .pipe(
        map((jsonData) => (jsonData ?? []).map((item) => this.mapApiPayloadToModel(item))),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Returns a single journal process definition by its id.
   * Returns null when the API responds with 404 or 204 No Content.
   */
  getById(id: string): Observable<JournalProcessDefinition | null> {
    return this.http
      .get<JournalProcessDefinitionApiModel | null>(this.apiUrl + '/' + id, this.httpOptions)
      .pipe(
        map((item) => (item ? this.mapApiPayloadToModel(item) : null)),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of(null);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Creates a new journal process definition. The id is always forced to the
   * empty guid so the backend can generate a new one.
   */
  addJournalProcessDefinition(
    definition: JournalProcessDefinition,
  ): Observable<JournalProcessDefinition> {
    const payload = this.mapModelToApiPayload({
      ...definition,
      journalProcessDefinitionId: EMPTY_GUID,
    });

    return this.http
      .post<AddJournalProcessDefinitionResponse | null>(this.apiUrl, payload, this.httpOptions)
      .pipe(
        map((response) => ({
          ...definition,
          journalProcessDefinitionId: this.getCreatedId(response),
        })),
        catchError((err: HttpErrorResponse) => this.handleError(err)),
      );
  }

  /**
   * Updates an existing journal process definition.
   */
  updateJournalProcessDefinition(
    id: string,
    definition: JournalProcessDefinition,
  ): Observable<JournalProcessDefinition> {
    const payload = this.mapModelToApiPayload({
      ...definition,
      journalProcessDefinitionId: id,
    });

    return this.http
      .put<unknown>(this.apiUrl + '/' + id, payload, this.httpOptions)
      .pipe(
        map(() => ({ ...definition, journalProcessDefinitionId: id })),
        catchError((err: HttpErrorResponse) => this.handleError(err)),
      );
  }

  /**
   * Removes a journal process definition by its id. There is no reference from
   * other records to a journal process definition, so no dependency check is
   * needed before deleting.
   */
  removeJournalProcessDefinition(id: string): Observable<unknown> {
    return this.http
      .delete(this.apiUrl + '/' + id, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  // ------------------------------------------------------------------------------
  // Helper methods for mapping between API payloads and the model.

  private mapApiPayloadToModel(
    apiModel: JournalProcessDefinitionApiModel,
  ): JournalProcessDefinition {
    return {
      journalProcessDefinitionId: apiModel.journalProcessDefinition_ID,
      bookingGroupId: apiModel.booking_Group_ID,
      name: apiModel.name,
      bookingDescription: apiModel.bookingDescription ?? null,
      institutionFinancialAdministrationNumber:
        apiModel.institutionFinancialAdministrationNumber ?? null,
      bookingNumber: apiModel.bookingNumber ?? null,
      journalNumber: apiModel.journalNumber ?? null,
      journalType: apiModel.journalType ?? null,
      employmentSelectionId: apiModel.employmentSelection_ID ?? null,
      applyWagePartioning: apiModel.applyWagePartioning,
      applyRedistribution: apiModel.applyRedistribution,
      bookByEmployment: apiModel.bookByEmployment,
      bookNegativeValuesIndicator: apiModel.bookNegativeValuesIndicator,
      retroactiveCalculationInPayrollPeriod: apiModel.retroactiveCalculationInPayrollPeriod,
      startDate: apiModel.startDate as JournalProcessDefinition['startDate'],
      endDate: apiModel.endDate as JournalProcessDefinition['endDate'],
      memo_Value: apiModel.memo_Value ?? null,
    };
  }

  private mapModelToApiPayload(
    model: JournalProcessDefinition,
  ): JournalProcessDefinitionApiPayload {
    return {
      journalProcessDefinition_ID: model.journalProcessDefinitionId,
      booking_Group_ID: model.bookingGroupId,
      name: model.name,
      bookingDescription: model.bookingDescription || null,
      institutionFinancialAdministrationNumber:
        model.institutionFinancialAdministrationNumber || null,
      bookingNumber: model.bookingNumber || null,
      journalNumber: model.journalNumber || null,
      journalType: model.journalType || null,
      employmentSelection_ID: model.employmentSelectionId || null,
      applyWagePartioning: model.applyWagePartioning,
      applyRedistribution: model.applyRedistribution,
      bookByEmployment: model.bookByEmployment,
      bookNegativeValuesIndicator: model.bookNegativeValuesIndicator,
      retroactiveCalculationInPayrollPeriod: model.retroactiveCalculationInPayrollPeriod,
      startDate: this.formatApiDate(model.startDate),
      endDate: this.formatApiDate(model.endDate),
      memo_Value: model.memo_Value || null,
    };
  }

  private getCreatedId(response: AddJournalProcessDefinitionResponse | null): string {
    return (
      response?.data ??
      response?.journalProcessDefinition_ID ??
      response?.journalProcessDefinitionId ??
      EMPTY_GUID
    );
  }

  private formatApiDate(value: unknown): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = moment(value as moment.MomentInput);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
  }

  private handleError(err: HttpErrorResponse) {
    const errorMessage = `An error has occurred: ${err.error?.message ?? err.message}`;
    console.error(errorMessage);
    return throwError(() => errorMessage);
  }
}
